import { useState, useEffect, useCallback, useRef } from 'react';

export interface FileItem {
  url: string;
  fileName: string;
  fileType: string;
}

interface FileViewerModalProps {
  visible: boolean;
  files: FileItem[];
  initialIndex?: number;
  onClose: () => void;
}

// Check if file is an image
const isImageFile = (fileType: string): boolean => {
  return fileType.startsWith('image/');
};

// Check if file is a PDF
const isPdfFile = (fileType: string): boolean => {
  return fileType === 'application/pdf' || fileType.includes('pdf');
};

// Check if file is an Office document
const isOfficeFile = (fileType: string): boolean => {
  return (
    fileType.includes('msword') ||
    fileType.includes('wordprocessingml') ||
    fileType.includes('excel') ||
    fileType.includes('spreadsheetml') ||
    fileType.includes('powerpoint') ||
    fileType.includes('presentationml')
  );
};

// Get appropriate viewer URL for documents
const getViewerUrl = (url: string, fileType: string): string => {
  // Use Google Docs Viewer for Office files and PDFs
  // This provides consistent rendering without triggering downloads
  if (isOfficeFile(fileType) || isPdfFile(fileType)) {
    return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;
  }
  return url;
};

// Zoom levels for click-to-zoom
const ZOOM_LEVELS = [1, 1.5, 2, 3, 4];

export function FileViewerModal({
  visible,
  files,
  initialIndex = 0,
  onClose,
}: FileViewerModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const lastClickTime = useRef(0);

  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      setLoading(true);
      setError(null);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [visible, initialIndex]);

  const resetView = useCallback(() => {
    setIsAnimating(true);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setLoading(true);
    setError(null);
    setTimeout(() => setIsAnimating(false), 300);
  }, []);

  const animateZoom = useCallback((newZoom: number, centerX?: number, centerY?: number) => {
    setIsAnimating(true);
    
    if (newZoom === 1) {
      setPosition({ x: 0, y: 0 });
    } else if (centerX !== undefined && centerY !== undefined && imageContainerRef.current) {
      // Zoom towards click point
      const rect = imageContainerRef.current.getBoundingClientRect();
      const containerCenterX = rect.width / 2;
      const containerCenterY = rect.height / 2;
      
      const clickOffsetX = centerX - containerCenterX;
      const clickOffsetY = centerY - containerCenterY;
      
      const zoomRatio = newZoom / zoom;
      const newX = position.x - clickOffsetX * (zoomRatio - 1);
      const newY = position.y - clickOffsetY * (zoomRatio - 1);
      
      setPosition({ x: newX, y: newY });
    }
    
    setZoom(newZoom);
    setTimeout(() => setIsAnimating(false), 300);
  }, [zoom, position]);

  // Keyboard navigation
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            resetView();
          }
          break;
        case 'ArrowRight':
          if (currentIndex < files.length - 1) {
            setCurrentIndex(currentIndex + 1);
            resetView();
          }
          break;
        case '+':
        case '=':
          e.preventDefault();
          animateZoom(Math.min(zoom + 0.5, 5));
          break;
        case '-':
          e.preventDefault();
          animateZoom(Math.max(zoom - 0.5, 1));
          break;
        case '0':
          resetView();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, currentIndex, files.length, onClose, resetView, zoom, animateZoom]);

  const currentFile = files[currentIndex];
  const hasMultipleFiles = files.length > 1;
  const isImage = currentFile ? isImageFile(currentFile.fileType) : false;
  const isPdf = currentFile ? isPdfFile(currentFile.fileType) : false;
  const isOffice = currentFile ? isOfficeFile(currentFile.fileType) : false;

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetView();
    }
  };

  const goToNext = () => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetView();
    }
  };

  const handleDownload = async () => {
    if (!currentFile) return;
    
    try {
      const response = await fetch(currentFile.url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = currentFile.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      const link = document.createElement('a');
      link.href = currentFile.url;
      link.download = currentFile.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Click to cycle through zoom levels
  const handleImageClick = (e: React.MouseEvent) => {
    if (isDragging) return;
    
    const now = Date.now();
    const isDoubleClick = now - lastClickTime.current < 300;
    lastClickTime.current = now;
    
    if (isDoubleClick) {
      // Double click - reset to fit
      resetView();
      return;
    }
    
    // Single click - cycle through zoom levels
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Find next zoom level
    const currentLevelIndex = ZOOM_LEVELS.findIndex(level => zoom <= level);
    const nextIndex = currentLevelIndex === -1 || currentLevelIndex === ZOOM_LEVELS.length - 1 
      ? 0 
      : currentLevelIndex + 1;
    const nextZoom = ZOOM_LEVELS[nextIndex];
    
    animateZoom(nextZoom, clickX, clickY);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    
    const rect = imageContainerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const delta = e.deltaY > 0 ? -0.3 : 0.3;
    const newZoom = Math.min(Math.max(zoom + delta, 1), 5);
    
    if (newZoom !== zoom) {
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      animateZoom(newZoom, clickX, clickY);
    }
  };

  if (!visible || !currentFile) return null;

  const renderImageContent = () => {
    const getCursor = () => {
      if (isDragging) return 'grabbing';
      if (zoom > 1) return 'grab';
      return 'zoom-in';
    };

    return (
      <div 
        ref={imageContainerRef}
        className="flex-1 flex items-center justify-center bg-[#0a0a0a] overflow-hidden relative"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ cursor: getCursor() }}
      >
        {/* Loading spinner */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a] z-10">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-gray-700 border-t-white rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-4 text-sm">Loading image...</p>
            </div>
          </div>
        )}
        
        {/* Error state */}
        {error ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Unable to load image</h3>
            <p className="text-gray-400 mb-6">{error}</p>
            <button 
              onClick={handleDownload} 
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download File
            </button>
          </div>
        ) : (
          <>
            {/* Image */}
            <img
              src={currentFile.url}
              alt={currentFile.fileName}
              className={`max-w-full max-h-full object-contain select-none ${isAnimating ? 'transition-transform duration-300 ease-out' : ''}`}
              style={{
                transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
              }}
              onClick={handleImageClick}
              onLoad={() => setLoading(false)}
              onError={() => {
                setError('Failed to load image');
                setLoading(false);
              }}
              draggable={false}
            />
            
            {/* Zoom indicator */}
            {zoom > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-black/70 rounded-full text-white text-sm font-medium backdrop-blur-sm">
                {Math.round(zoom * 100)}%
              </div>
            )}
            
            {/* Hint overlay - shows briefly */}
            {!loading && zoom === 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-lg text-white/70 text-sm backdrop-blur-sm pointer-events-none animate-fade-out">
                Click to zoom • Scroll to zoom • Drag to pan
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderPdfContent = () => {
    // Use Google Docs Viewer for PDFs to prevent download behavior
    const viewerUrl = getViewerUrl(currentFile.url, currentFile.fileType);
    
    return (
      <div className="flex-1 flex flex-col bg-gray-100 relative z-0">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading PDF...</p>
          </div>
        )}
        {error ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-white">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load PDF</h3>
            <p className="text-gray-500 mb-6 max-w-sm">{error}</p>
            <button 
              onClick={handleDownload} 
              className="flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors font-medium shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download PDF
            </button>
          </div>
        ) : (
          <iframe
            src={viewerUrl}
            className="flex-1 w-full border-0"
            title={currentFile.fileName}
            onLoad={() => setLoading(false)}
            onError={() => {
              setError('Failed to load PDF viewer');
              setLoading(false);
            }}
          />
        )}
      </div>
    );
  };

  const renderOfficeContent = () => {
    const viewerUrl = getViewerUrl(currentFile.url, currentFile.fileType);
    return (
      <div className="flex-1 flex flex-col bg-white relative z-0">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading document...</p>
          </div>
        )}
        <iframe
          src={viewerUrl}
          className="flex-1 w-full border-0"
          title={currentFile.fileName}
          onLoad={() => setLoading(false)}
        />
      </div>
    );
  };

  const renderUnsupportedContent = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50">
      <div className="w-24 h-24 rounded-2xl bg-gray-200 flex items-center justify-center mb-6">
        <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">Preview not available</h3>
      <p className="text-gray-500 mb-6 max-w-sm">
        This file type cannot be previewed. Download to view it on your device.
      </p>
      <button 
        onClick={handleDownload} 
        className="flex items-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors font-medium shadow-lg"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Download File
      </button>
    </div>
  );

  const renderContent = () => {
    if (isImage) return renderImageContent();
    if (isPdf) return renderPdfContent();
    if (isOffice) return renderOfficeContent();
    return renderUnsupportedContent();
  };

  const isDarkMode = isImage;

  return (
    <>
      {/* Custom styles for animations */}
      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-out {
          animation: fadeOut 3s ease-out forwards;
        }
      `}</style>
      
      <div className="fixed inset-0 z-[9999] flex flex-col" role="dialog" aria-modal="true">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/90 backdrop-blur-sm" 
          onClick={onClose}
          aria-hidden="true"
        />
        
        {/* Modal Content */}
        <div className="relative z-10 flex flex-col h-full m-2 sm:m-4 md:m-6">
          {/* Header */}
          <div className={`flex items-center px-3 sm:px-4 py-2.5 sm:py-3 rounded-t-xl ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white shadow-sm'}`}>
            {/* Close button */}
            <button 
              onClick={onClose} 
              className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
              title="Close (Esc)"
              aria-label="Close"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* File info */}
            <div className="flex-1 mx-3 sm:mx-4 min-w-0">
              <h2 className={`text-sm sm:text-base font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {currentFile.fileName}
              </h2>
              {hasMultipleFiles && (
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {currentIndex + 1} of {files.length}
                </p>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-1">
              {/* Zoom controls for images */}
              {isImage && (
                <div className="hidden sm:flex items-center gap-1 mr-2 pr-3 border-r border-gray-600">
                  <button
                    onClick={() => animateZoom(Math.max(zoom - 0.5, 1))}
                    className="p-2 rounded-lg hover:bg-white/10 text-white transition-all disabled:opacity-30"
                    title="Zoom out (-)"
                    disabled={zoom <= 1}
                    aria-label="Zoom out"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                  </button>
                  <button
                    onClick={resetView}
                    className={`px-2 py-1 rounded text-sm font-mono transition-all ${zoom === 1 ? 'text-gray-500' : 'text-white hover:bg-white/10'}`}
                    title="Reset zoom (0)"
                    aria-label="Reset zoom"
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    onClick={() => animateZoom(Math.min(zoom + 0.5, 5))}
                    className="p-2 rounded-lg hover:bg-white/10 text-white transition-all disabled:opacity-30"
                    title="Zoom in (+)"
                    disabled={zoom >= 5}
                    aria-label="Zoom in"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                    </svg>
                  </button>
                </div>
              )}
              
              {/* Download button */}
              <button 
                onClick={handleDownload}
                className={`p-2 rounded-lg transition-all ${isDarkMode ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                title="Download file"
                aria-label="Download"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className={`flex-1 flex relative overflow-hidden ${isDarkMode ? '' : 'bg-white'} rounded-b-xl`}>
            {renderContent()}
            
            {/* Navigation Arrows */}
            {hasMultipleFiles && currentIndex > 0 && (
              <button
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/70 hover:scale-110 flex items-center justify-center text-white transition-all z-[100] backdrop-blur-sm"
                onClick={goToPrevious}
                title="Previous (←)"
                aria-label="Previous file"
              >
                <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            {hasMultipleFiles && currentIndex < files.length - 1 && (
              <button
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/70 hover:scale-110 flex items-center justify-center text-white transition-all z-[100] backdrop-blur-sm"
                onClick={goToNext}
                title="Next (→)"
                aria-label="Next file"
              >
                <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            
            {/* Thumbnail strip for multiple files */}
            {hasMultipleFiles && files.length <= 10 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-xl backdrop-blur-sm z-[100]">
                {files.map((file, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      resetView();
                    }}
                    className={`w-12 h-12 rounded-lg overflow-hidden transition-all ${
                      index === currentIndex 
                        ? 'ring-2 ring-white scale-110' 
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    title={file.fileName}
                  >
                    {isImageFile(file.fileType) ? (
                      <img 
                        src={file.url} 
                        alt={file.fileName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
